import { useEffect, useState, Fragment } from 'react'
import {
  AspectRatio,
  VStack,
  Text,
  useDisclosure,
  Button,
  Link,
} from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'

import { useCourseContext } from '@/features/Courses/contexts/CourseContext'
import TitleDrawer from '@/features/Courses/components/TitleDrawer'
import {
  logCTAEvent,
  logPreviewEvent,
} from '@/features/Courses/helpers/gtmEvent'

import { breakpoints } from '@/styles/chakraTheme'

function CourseTitle(): React.ReactElement {
  const course = useCourseContext()

  const { ref, inView } = useInView({
    threshold: 0.2,
  })
  const drawerDisclosure = useDisclosure()

  const [isEnabledDrawer, setEnableDrawer] = useState(false)

  useEffect(() => {
    if (window.innerWidth < parseInt(breakpoints.md)) {
      setEnableDrawer(true)
    }

    const resizeHandler = () => {
      if (window.innerWidth < parseInt(breakpoints.md)) {
        setEnableDrawer(true)
      } else {
        setEnableDrawer(false)
      }
    }

    window.addEventListener('resize', resizeHandler)

    return () => window.removeEventListener('resize', resizeHandler)
  }, [])

  useEffect(() => {
    if (inView) {
      drawerDisclosure.onClose()
    } else {
      drawerDisclosure.onOpen()
    }
  }, [inView])

  return (
    <Fragment>
      <VStack
        ref={ref}
        spacing={{
          base: 4,
          sm: 6,
          md: 8,
        }}
        sx={{
          alignItems: 'stretch',
          py: {
            base: 7,
            sm: 8,
          },
        }}
      >
        <AspectRatio
          ratio={713 / 412}
          sx={{
            maxW: {
              base: '100%',
              md: '713px',
            },
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <iframe
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
            }}
            src={`https://www.youtube.com/embed/${course.video}?autoplay=1&modestbranding=1&rel=0&color=red&fs=1`}
            title={`YouTube video player: ${course.title}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        </AspectRatio>
        <VStack spacing="3" sx={{ alignItems: 'stretch' }}>
          <Text
            as="h1"
            sx={{
              fontWeight: 'bold',
              fontSize: {
                base: '2xl',
                md: '32px',
              },
            }}
          >
            {course.title}
          </Text>
          <VStack
            spacing="3"
            sx={{
              alignItems: 'stretch',
              display: {
                base: 'block',
                md: 'none',
              },
            }}
          >
            <Text
              sx={{
                fontSize: {
                  base: 'md',
                  md: 'lg',
                },
                fontWeight: 'light',
                color: 'gray.700',
              }}
            >
              {course.subtitle}
            </Text>
            <Text
              sx={{
                mt: 2,
                fontSize: {
                  base: 'xl',
                  sm: '2xl',
                  md: '3xl',
                },
                fontWeight: 'bold',
                color: 'primary.500',
              }}
            >
              {course.price}
            </Text>
            <VStack
              sx={{
                mt: '24px !important',
              }}
            >
              <Button
                as={Link}
                isExternal
                href={course.coursePageLink}
                onClick={() => {
                  logCTAEvent(course.slug)
                }}
                size="lg"
                sx={{
                  textDecoration: 'none !important',
                  w: 'full',
                  h: '60px',
                  fontSize: 'xl',
                }}
              >
                ชำระเงินเพื่อเริ่มเรียน
              </Button>
              {course.previewLink && (
                <Button
                  as={Link}
                  isExternal
                  href={course.previewLink}
                  variant="outline"
                  size="lg"
                  sx={{
                    textDecoration: 'none !important',
                    w: 'full',
                    h: '56px',
                    fontSize: 'xl',
                  }}
                  onClick={() => {
                    logPreviewEvent(course.slug)
                  }}
                >
                  ทดลองเรียนฟรี !
                </Button>
              )}
            </VStack>
          </VStack>
        </VStack>
      </VStack>
      <TitleDrawer
        course={course}
        disclosure={{
          ...drawerDisclosure,
          isOpen: isEnabledDrawer && drawerDisclosure.isOpen,
        }}
      />
    </Fragment>
  )
}

export default CourseTitle
